import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import styled from 'styled-components'
import { Tooltip } from 'antd'
import { colours } from '@qflow/theme'
import { ChevronDown } from 'portal/lib/primitives/icons'
import { useMixpanelEvents } from 'portal/components/Sidebar/Mixpanel'

export function SideBarGroup({
  children,
  groupName = '',
  groupIcon = null,
  active = false,
  disabled = false,
  UpgradeToProComponent = null
}) {
  const [isOpen, setIsOpen] = useState(active)
  const { accountId, projectId } = useParams()

  const { trackNavGroupClick } = useMixpanelEvents()
  const isCollapsed = useSelector(state => state.ui.data.sidebarCollapsed)

  useEffect(() => {
    if (isCollapsed) {
      setIsOpen(false)
    }
  }, [isCollapsed])

  const togglePanel = e => {
    e.preventDefault()

    trackNavGroupClick(projectId, accountId, groupName, !isOpen)
    setIsOpen(!isOpen)
  }

  const handleMouseEnter = e => {
    setIsOpen(true)
  }

  const handleMouseLeave = e => {
    setIsOpen(false)
  }

  if (disabled && !UpgradeToProComponent) {
    return (
      <DisabledGroup>
        <div>
          <Tooltip
            title={`${groupName} feature not enabled for this Project`}
            placement="right"
          >
            <SummaryLeft>
              <GroupIcon>{groupIcon}</GroupIcon>
              {!isCollapsed && <div>{groupName}</div>}
            </SummaryLeft>
          </Tooltip>
        </div>
      </DisabledGroup>
    )
  } else if (disabled && UpgradeToProComponent) {
    return (
      <DisabledGroup>
        <div>
          <SummaryLeft>
            <GroupIcon>{groupIcon}</GroupIcon>
            {!isCollapsed && <div>{groupName}</div>}
          </SummaryLeft>
          {!isCollapsed && UpgradeToProComponent && (
            <UpgradeToProComponent feature={groupName} />
          )}
        </div>
      </DisabledGroup>
    )
  }

  return isCollapsed ? (
    <StyledDetails
      open={isOpen}
      active={active}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <summary>
        <SummaryLeft>
          <GroupIcon>{groupIcon}</GroupIcon>
        </SummaryLeft>
      </summary>
      <CollapsedMenuOptions>
        <div style={{ paddingLeft: '4px' }}>{groupName}</div>
        {children}
      </CollapsedMenuOptions>
    </StyledDetails>
  ) : (
    <StyledDetails
      open={isOpen}
      active={active}
      disabled={disabled}
      onClick={togglePanel}
    >
      <summary>
        <SummaryLeft>
          <GroupIcon>{groupIcon}</GroupIcon>
          <div>{groupName}</div>
        </SummaryLeft>
        <ChevronDown size={'12'} />
      </summary>
      {children}
    </StyledDetails>
  )
}

const StyledDetails = styled.details`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  list-style: none;
  font-size: 14px;
  font-weight: 700;
  color: ${({ active, theme }) =>
    active ? `${theme.colours.TEAL} !important` : 'rgba(255,255,255,0.8)'};
  cursor: pointer;
  outline: none;
  user-select: none;
  padding: 0.25rem 1rem 0.25rem 0.75rem;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: ${colours.CONTROL_PANEL_GREY};
  }

  &[open] {
    SUMMARY > SVG {
      transform: rotate(180deg) !important;
    }
  }

  & > summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
  }
`

const SummaryLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const DisabledGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  list-style: none;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  outline: none;
  user-select: none;
  padding: 0.625rem 1rem 0.625rem 0.75rem;

  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0;
  }
`
const GroupIcon = styled.div`
  min-width: 20px;
  max-height: 18px;
  text-align: center;
`
const CollapsedMenuOptions = styled.div`
  position: absolute;
  z-index: 10;
  left: 100%;
  top: 0;
  background: ${colours.CORE_NAVY}F6;
  padding: 0.75rem;
  border: 1px solid #ffffff33;
  border-radius: 8px;
  box-shadow: 0px 1px 3px 0px #00000026;
`
